<template>
  <v-form ref="form">
    <v-row>
      <v-col cols="12">
        <v-card class="pa-8">
          <v-row>
            <v-col cols="12" sm="8" lg="10">
              <v-text-field
                v-model="model.title"
                variant="outlined"
                label="Nome"
                :rules="[required]"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="4" lg="2">
              <v-btn
                color="primary"
                block
                :loading="loader.loading"
                size="x-large"
                @click="submit"
              >
                {{ model.id ? 'Salvar' : 'Criar' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col cols="12">
        <v-card class="pa-8">
          <v-row>
            <v-col>
              <div class="d-flex align-center ga-2">
                <div class="text-h6">
                  Permissões
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="filters.title"
                label="Nome"
                variant="outlined"
                dense
                color="orange lighten-2"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="filters.status"
                label="Situação"
                :items="statusFilterItems"
                variant="outlined"
                hide-details
                dense
                color="orange lighten-2"
                density="compact"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              style="display: grid"
              :style="{
                gridTemplateColumns: xs ? '1fr' : 'repeat(4, 1fr)',
              }"
            >
              <div
                v-for="ability in filteredAbilities"
                :key="ability.name"
                class="d-flex align-center ga-1"
              >
                <v-checkbox
                  v-model="model.abilities"
                  :label="ability.title"
                  :value="ability"
                  :value-comparator="(a, b) => a.name === b.name"
                  color="primary"
                  hide-details
                  multiple
                />
                <t-tooltip v-if="ability.name.includes('manage')">
                  Esta permissão agrupa <strong>todas</strong> as ações possíveis em {{ ability.title.split('Gerenciar ')[1] }}
                </t-tooltip>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-form>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { useLoader } from '@/stores/loader'
import Role from '@/types/Role'
import Ability from '@/types/Ability'
import AbilityService from '@/services/AbilityService'
import { useDisplay } from 'vuetify'
import TTooltip from '@/components/TTooltip.vue'
import { required } from '@/helpers/rules/required'

const loader = useLoader()
const { xs } = useDisplay()

const model = defineModel<Role>({ required: true })

const emit = defineEmits([ 'submit' ])

// --- Filters ---
const filters = ref({ status: '', title: '' })

const statusFilterItems = [
  { title: 'Todas', value: '' },
  { title: 'Concedidas', value: true },
  { title: 'Não Concedidas', value: false },
]

// --- Abilities ---
const abilities = shallowRef<Ability[]>([])

const filteredAbilities = computed(() => {
  const { status, title } = filters.value

  return abilities.value.filter((a) => {
    // filter out the the special "All Abilities"
    if (a.name === '*') return false

    // filter by status
    if (!a.title.toLowerCase().includes(title.toLowerCase())) return false

    // filter by title
    return typeof status === 'string' || status === model.value.abilities?.some(b => a.name === b.name)
  })
})

async function fetchAbilities() {
  try {
    loader.increment()

    const response = await (new AbilityService()).index(1, '', 1000)

    abilities.value = response.data.data
  } finally {
    loader.decrement()
  }
}

// --- Form ---
const form = useTemplateRef('form')

async function submit() {
  await form.value?.validate()

  if (!form.value?.isValid) return

  // Set the role name based on its title
  // TODO: this isn't good and an input for the name would probably be better
  if (!model.value.id) {
    model.value.name = model.value.title.toLowerCase()
  }

  emit('submit')
}

onMounted(fetchAbilities)
</script>
